import { Avatar, Flex, Group, Stack, useMantineTheme } from '@mantine/core';
import { AIAvatar } from '@repo/common/components/AI/Avatar';
import { UserAvatar } from '@repo/common/components/UserAvatar';
import { Text, Title } from '@repo/foundations';
import { useMemo } from 'react';
import type { IAiChat } from '../../api/hooks/aiChats';
import { FilterOptionType } from '../../components/Filter';
import { CreatedAtRender } from '../../components/TableV2/render';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';
import { formatHoursToHm } from '../../features/AiImpactDashboard/utils';
import { formatRelativeTime } from '../../utils/time';
import { CategoriesColumn } from './components/CategoriesColumn';
import { QueriedResourcesColumn } from './components/QueriedResourcesColumn.tsx';
import { SentimentIcon } from './components/SentimentIcon';
import { ComplexityPopover, RatingPopover } from './utils/popoverHelpers.tsx';

export function useColumns(): ExtendedDataTableColumn<IAiChat>[] {
	const theme = useMantineTheme();
	const columns: ExtendedDataTableColumn<IAiChat>[] = useMemo(
		() => [
			{
				accessor: 'prompt',
				titleLabel: 'Chats',
				render: (record: IAiChat) => {
					const formattedCreatedAt = formatRelativeTime(record.created_at);
					return (
						<Group maw={theme.other.space[90]} spacing="xs" noWrap>
							<UserAvatar
								size="sm"
								user={{
									id: record.user_id,
									display_name: record.user_name || 'Unknown user',
									first_name: '',
									last_name: '',
									profile_picture: record.user_profile_picture,
								}}
							/>
							<Stack spacing={0} style={{ flex: 1, minWidth: 0 }}>
								<Title size="sm" truncate>
									{record.title || record.prompt?.[0] || ''}
								</Title>
								<Text size="xs" color="text/secondary/default">
									{`Asked by ${record.user_name || 'Unknown user'} · ${formattedCreatedAt} ${formattedCreatedAt === 'now' ? '' : 'ago'}`}
								</Text>
							</Stack>
						</Group>
					);
				},
				width: 'auto',
			},
			{
				accessor: 'time_saved_hours',
				titleLabel: 'Time savings',
				textAlignment: 'right',
				render: (record: IAiChat) => {
					if (!record.time_saved_hours) {
						return null;
					}

					return (
						<Text align="end" size="sm" color="text/primary/default">
							{formatHoursToHm(record.time_saved_hours)}
						</Text>
					);
				},
			},
			{
				accessor: 'queried_resources_count',
				titleLabel: 'Queried resources',
				sortable: false,
				render: (record: IAiChat) => <QueriedResourcesColumn record={record} />,
			},
			{
				accessor: 'time_savings_categories',
				titleLabel: 'Categories',
				sortable: false,
				render: (record: IAiChat) => <CategoriesColumn record={record} />,
			},
			{
				accessor: 'complexity',
				titleLabel: 'Complexity',
				sortable: false,
				render: (record: IAiChat) => {
					if (!record.complexity) {
						return null;
					}

					return <ComplexityPopover complexity={record.complexity} />;
				},
			},
			{
				accessor: 'score',
				titleLabel: 'System rating',
				sortable: false,
				render: (record: IAiChat) => {
					if (!record.score) {
						return null;
					}

					return <RatingPopover score={record.score} />;
				},
			},
			{
				accessor: 'sentiments',
				titleLabel: 'Feedback',
				sortable: false,
				render: (record: IAiChat) => {
					const validSentiments = record.sentiments?.filter(
						(s): s is string => s !== null && s !== undefined
					);
					if (!validSentiments || validSentiments.length === 0) {
						return null;
					}

					return (
						<Flex justify="start" gap="xs">
							{validSentiments.map((sentiment, index) => (
								<SentimentIcon key={index} sentiment={sentiment} />
							))}
						</Flex>
					);
				},
			},
			{
				accessor: 'persona_name',
				titleLabel: 'Persona',
				sortable: false,
				render: (record: IAiChat) => {
					const personaName = record.persona_name || 'Secoda AI';
					const hasPersonaAvatar = Boolean(record.persona_avatar_url);
					const isDefaultPersona = !record.persona_id;
					const personaIcon =
						hasPersonaAvatar && !isDefaultPersona ? (
							<Avatar
								src={record.persona_avatar_url ?? undefined}
								alt={personaName}
								radius="xl"
							/>
						) : (
							<AIAvatar size="md" speed="none" />
						);

					return (
						<Flex align="center" gap="xs">
							{personaIcon}
							<Text size="sm" color="text/primary/default">
								{personaName}
							</Text>
						</Flex>
					);
				},
			},
			{
				accessor: 'created_at',
				titleLabel: 'Asked at',
				render: (record) => <CreatedAtRender record={record} />,
				width: 200,
				filterOptionType: FilterOptionType.CREATED_TIME,
			},
		],
		[]
	);

	return columns;
}
